import { styled } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchIcon } from './icons';
import { tokens } from '../../design';

/**
 * Top bar — 54px high, matches `.topbar` in
 * `design/mockups/admin/layout.html` (LOCK 11).
 *
 * v1 renders a static title + search placeholder + a CONNECTING pill.
 * Real per-page title injection (via context) + real WebSocket
 * connection state land in ADMIN-15+.
 */

const TopBar = styled('header')({
  height: tokens.layout.headerHeight,
  padding: '0 22px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  background: tokens.surface.base,
  borderBottom: `1px solid ${tokens.border.default}`,
  flexShrink: 0,
});

const Title = styled('div')({
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: '-0.2px',
  color: tokens.text.primary,
});

const SearchWrap = styled('div')({
  marginLeft: 14,
  flex: 1,
  maxWidth: 280,
  position: 'relative',
});

const SearchInput = styled('input')({
  width: '100%',
  height: 34,
  background: tokens.surface.elevated,
  border: `1px solid ${tokens.border.default}`,
  borderRadius: 999,
  padding: '0 12px 0 34px',
  fontSize: 12,
  color: tokens.text.primary,
  outline: 'none',
  fontFamily: 'inherit',
  '&:focus': {
    borderColor: tokens.brand.primary,
  },
});

const SearchIconWrap = styled('span')({
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 13,
  height: 13,
  color: tokens.text.tertiary,
  pointerEvents: 'none',
  '& svg': {
    width: 13,
    height: 13,
  },
});

const Actions = styled('div')({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const LivePill = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 9px',
  background: tokens.state.successBg,
  color: tokens.state.success,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
});

const LiveDot = styled('span')({
  width: 6,
  height: 6,
  borderRadius: 999,
  background: tokens.state.success,
  boxShadow: '0 0 0 3px rgba(22, 163, 106, 0.2)',
});

export const Header = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <TopBar>
      <Title>{t('nav.dashboard')}</Title>
      <SearchWrap>
        <SearchIconWrap>
          <SearchIcon />
        </SearchIconWrap>
        <SearchInput
          placeholder={t('header.searchPlaceholder')}
          aria-label={t('header.searchPlaceholder')}
        />
      </SearchWrap>
      <Actions>
        <LivePill>
          <LiveDot />
          {t('header.statusConnecting')}
        </LivePill>
      </Actions>
    </TopBar>
  );
};
